import { formatAuditAction as formatAction } from '@/lib/auditDisplay';
/**
 * NotificationBell Component
 *
 * Bell icon with unread badge for superadmin audit notifications.
 * Uses Radix UI Popover for accessible dropdown interaction.
 */

import React, { useState } from 'react';
import {
  Bell,
  X,
  Check,
  ExternalLink,
  Volume2,
  VolumeX,
  LogIn,
  UserPlus,
  KeyRound,
  FolderPlus,
  Building2,
  Trash2,
  Truck,
  Pencil,
  MapPin,
  Shield,
  LucideIcon
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useSuperadminNotificationsSafe } from '../../contexts/SuperadminNotificationContext';
import { useRBAC } from '../../contexts/RBACContext';
import { cn } from '../../lib/utils';
import type { AuditLog } from '../../types';

// Get icon based on action type (matches AuditLogs.tsx pattern)
function getActionIcon(action: string): LucideIcon {
  if (action.includes('Login') || action.includes('Logout')) return LogIn;
  if (action.includes('Delete') || action.includes('Remove')) return Trash2;
  if (action.includes('Create') || action.includes('Add') || action.includes('Invite')) return UserPlus;
  if (action.includes('Update') || action.includes('Change') || action.includes('Modified')) return Pencil;
  if (action.includes('Password') || action.includes('Reset')) return KeyRound;
  if (action.includes('Group')) return FolderPlus;
  if (action.includes('Company')) return Building2;
  if (action.includes('Unit')) return Truck;
  return Shield;
}

// Format relative time
function formatRelativeTime(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) return 'just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

// Get severity icon color classes (matches audit page styling)
function getSeverityIconColor(severity: string): string {
  switch (severity) {
    case 'critical': return 'text-error bg-zinc-800/60';
    case 'warning': return 'text-warning bg-zinc-800/60';
    default: return 'text-muted-foreground bg-zinc-800/60';
  }
}

// Format role for display (e.g., "group_admin" → "Group Admin")
function formatRole(role: string): string {
  if (!role || role === '-') return '';
  return role.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
}



// Determine badge color based on action type (matches Overview.tsx)
function getActionStyle(action: string): string {
  const a = action.toLowerCase();
  // Check failures/errors first (red)
  if (a.includes('failed') || a.includes('rejected') || a.includes('deleted') || a.includes('removed') || a.includes('suspicious')) {
    return 'bg-red-500/20 text-red-400 border-red-500/30';
  }
  // Success states (green)
  if (a.includes('login') || a.includes('success') || a.includes('approved') || a.includes('activated')) {
    return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
  }
  // Creation states (blue)
  if (a.includes('created') || a.includes('added') || a.includes('invited') || a.includes('create')) {
    return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
  }
  // Update/modification states (amber)
  if (a.includes('updated') || a.includes('changed') || a.includes('modified') || a.includes('update')) {
    return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
  }
  return 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30';
}

// Individual notification item - compact 2-row layout matching Overview.tsx
function NotificationItem({ log }: { log: AuditLog }) {
  const formattedAction = formatAction(log.action);
  const Icon = getActionIcon(log.action);
  const displayName = log.name || log.user?.split('@')[0] || 'System';
  const formattedRole = formatRole(log.role);

  return (
    <div className="px-3 py-2 hover:bg-muted/50 transition-colors">
      <div className="flex items-start gap-2">
        {/* Icon */}
        <div className={cn('p-1 rounded-md flex-shrink-0 mt-0.5', getSeverityIconColor(log.severity))}>
          <Icon className="w-3 h-3" />
        </div>

        {/* Content - 2 rows max */}
        <div className="min-w-0 flex-1 space-y-0.5">
          {/* Row 1: Action Badge + Name + Role Badge */}
          <div className="flex flex-wrap items-center gap-1 text-xs">
            <span className={cn('inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] font-semibold uppercase tracking-wide', getActionStyle(log.action))}>
              {formattedAction}
            </span>
            <span className="font-medium text-foreground truncate max-w-[100px]">{displayName}</span>
            {formattedRole && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-zinc-700/50 text-zinc-300 text-[10px]">
                {formattedRole}
              </span>
            )}
          </div>

          {/* Row 2: Time + Company + Location - no truncation, allow full width */}
          <div className="flex flex-wrap items-center gap-x-1.5 text-[10px] text-muted-foreground/70">
            <span>{formatRelativeTime(log.timestamp)}</span>
            {log.company && (
              <>
                <span>•</span>
                <span className="text-[#8FB560]">{log.company}</span>
              </>
            )}
            {log.location && log.location !== 'Local' && (
              <>
                <span>•</span>
                <span className="inline-flex items-center gap-0.5">
                  <MapPin className="w-2 h-2" />
                  <span>{log.location}</span>
                </span>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Empty state component
function EmptyState({ lastPollTime }: { lastPollTime: Date | null }) {
  return (
    <div className="px-4 py-8 text-center text-muted-foreground">
      <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
      <p>No recent activity</p>
      {lastPollTime && (
        <p className="text-xs mt-1">
          Last checked: {formatRelativeTime(lastPollTime.toISOString())}
        </p>
      )}
    </div>
  );
}

// Popover header component
function PopoverHeader({
  isEnabled,
  setIsEnabled,
  hasNotifications,
  markAllAsRead,
  onClose
}: {
  isEnabled: boolean;
  setIsEnabled: (enabled: boolean) => void;
  hasNotifications: boolean;
  markAllAsRead: () => void;
  onClose: () => void;
}) {
  return (
    <div className="flex items-center justify-between border-b border-border px-4 py-3">
      <div className="flex items-center gap-2">
        <h3 className="font-semibold text-foreground">Activity Feed</h3>
      </div>
      <div className="flex items-center gap-1">
        {/* Toggle notifications */}
        <button
          onClick={() => setIsEnabled(!isEnabled)}
          className={cn(
            'p-1.5 rounded transition-colors',
            isEnabled
              ? 'text-success hover:bg-success/10'
              : 'text-muted-foreground hover:bg-muted'
          )}
          title={isEnabled ? 'Pause notifications' : 'Resume notifications'}
        >
          {isEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
        </button>

        {/* Mark all read */}
        {hasNotifications && (
          <button
            onClick={markAllAsRead}
            className="p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            title="Mark all as read"
          >
            <Check className="h-4 w-4" />
          </button>
        )}

        {/* Close */}
        <button
          onClick={onClose}
          className="p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

// Popover footer component
function PopoverFooter({
  onViewAll
}: {
  onViewAll: () => void;
}) {
  return (
    <div className="flex items-center justify-end border-t border-border px-4 py-3">
      <button
        onClick={onViewAll}
        className="flex items-center gap-1 text-xs text-[#8FB560] hover:text-[#a0c671] transition-colors"
      >
        View all activity
        <ExternalLink className="h-3 w-3" />
      </button>
    </div>
  );
}

export function NotificationBell() {
  const navigate = useNavigate();
  const { isSuperadmin, isAdmin, isCompanyAdmin, isGroupAdmin, user } = useRBAC();
  const notifications = useSuperadminNotificationsSafe();

  const [isOpen, setIsOpen] = useState(false);

  // Check if user is authenticated
  const isAuthenticated = !!user;
  // Check if user has admin-level access
  const hasAdminAccess = isSuperadmin || isAdmin || isCompanyAdmin || isGroupAdmin;

  // Don't render if not authenticated or notifications not available
  if (!isAuthenticated || !notifications) {
    return null;
  }

  const {
    recentNotifications,
    unreadCount,
    markAllAsRead,
    isEnabled,
    setIsEnabled,
    lastPollTime,
  } = notifications;

  // Mark as read when opening
  const handleOpenChange = (open: boolean) => {
    if (open && unreadCount > 0) {
      markAllAsRead();
    }
    setIsOpen(open);
  };

  // Navigate to appropriate audit page based on user role
  const handleViewAll = () => {
    setIsOpen(false);
    if (isSuperadmin) {
      navigate('/settings/global-audit');
    } else if (hasAdminAccess) {
      navigate('/settings/audit');
    } else {
      navigate('/settings');
    }
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          className={cn(
            'relative p-2 rounded-lg transition-colors',
            'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800',
            isOpen && 'bg-zinc-800 text-zinc-100'
          )}
          title={isEnabled ? 'Notifications' : 'Notifications (paused)'}
        >
          <Bell className="h-5 w-5" />

          {/* Unread Badge */}
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-error/80 px-1 text-[10px] font-bold text-white">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-80 sm:w-96 p-0 rounded-lg border border-border bg-card shadow-xl"
      >
        {/* Header */}
        <PopoverHeader
          isEnabled={isEnabled}
          setIsEnabled={setIsEnabled}
          hasNotifications={recentNotifications.length > 0}
          markAllAsRead={markAllAsRead}
          onClose={handleClose}
        />

        {/* Notifications List - shows ~5 items, scrollable to see all 15 */}
        <div className="max-h-[280px] overflow-y-auto">
          {recentNotifications.length === 0 ? (
            <EmptyState lastPollTime={lastPollTime} />
          ) : (
            <div className="divide-y divide-border">
              {recentNotifications.slice(0, 15).map((log: AuditLog) => (
                <NotificationItem key={log.id} log={log} />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {recentNotifications.length > 0 && (
          <PopoverFooter
            onViewAll={handleViewAll}
          />
        )}
      </PopoverContent>
    </Popover>
  );
}

export default NotificationBell;
